import { supabase } from './supabase';

// Ensure the SDK has a valid, non-expired session before every DB call.
// In Supabase JS v2, getSession() refreshes the token if needed and
// guarantees the internal PostgREST fetch will include Authorization: Bearer.
async function requireSession() {
  const { data: { session }, error } = await supabase.auth.getSession();

  console.debug(
    '[Supabase] session uid before DB call:',
    session?.user?.id ?? 'NULL — not authenticated',
    '| expires_at:', session?.expires_at ?? '-',
  );

  if (error) {
    console.error('[Supabase] getSession error:', error);
    throw error;
  }
  if (!session) {
    throw new Error('No active Supabase session — user must be signed in');
  }
  return session;
}

// The profiles row is created by the handle_new_user() DB trigger on signup
// (SECURITY DEFINER, runs server-side — covers guest/anonymous sign-in too).
// There is deliberately no client-side INSERT policy on profiles (see
// 20260720000001_profiles_grant.sql), so this only verifies the row exists
// via the permitted "own row select" policy instead of attempting an insert
// that RLS would always reject with 42501.
//
// Returns the row it verified ({ id, created_at, role }) or null. `role` rides
// along so the app does not read the SAME ROW twice on every launch — the
// admin check used to be a second round trip to profiles, in series, on the
// critical path of the first paint.
//
// `created_at` is
// what tells a brand-new account from a returning one: it is written by the
// trigger at sign-up, server-side, so it cannot be faked by a cleared
// localStorage or a second device (see trackEvent in lib/appEvents.js).
export async function ensureProfile(userId) {
  await requireSession();

  const { data, error } = await supabase
    .from('profiles')
    .select('id, created_at, role')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    console.error('[Supabase] ensureProfile select →', error.code, error.message);
  } else if (!data) {
    console.error('[Supabase] ensureProfile → no profile row for', userId, '— handle_new_user() trigger may not have run');
  }
  return data ?? null;
}

export async function loadOrCreatePortfolio(userId) {
  await requireSession();

  const { data, error: selectError } = await supabase
    .from('portfolios')
    .select('id, name')
    .eq('user_id', userId)
    .limit(1)
    .maybeSingle();

  if (selectError) {
    console.error('[Supabase] SELECT portfolios →', selectError.code, selectError.message);
    throw selectError;
  }

  if (data) return data;

  // No portfolio yet → create default
  await requireSession();               // re-verify before write

  const { data: created, error: insertError } = await supabase
    .from('portfolios')
    .insert({ user_id: userId, name: 'התיק שלי' })
    .select('id, name')
    .single();

  if (insertError) {
    console.error('[Supabase] INSERT portfolios →', insertError.code, insertError.message);
    throw insertError;
  }

  return created;
}

// ─── Guest / demo seeding ─────────────────────────────────────────────────────
// A small, well-known demo portfolio + watchlist so every screen is populated
// for anonymous testers. Buy prices are plausible round levels — labelled
// clearly as demo data via the guest banner and is_demo profile flag.
export const DEMO_POSITIONS = [
  { symbol: "AAPL", quantity: 15, buy_price: 180 },
  { symbol: "MSFT", quantity: 8,  buy_price: 380 },
  { symbol: "NVDA", quantity: 10, buy_price: 110 },
  { symbol: "AMZN", quantity: 12, buy_price: 155 },
];
export const DEMO_WATCHLIST = ["GOOGL", "META", "TSLA"];

// Insert the demo positions for a guest's own portfolio. RLS scopes the write
// to the caller's portfolio; no service-role, no cross-user access.
export async function seedDemoPositions(portfolioId) {
  await requireSession();

  const rows = DEMO_POSITIONS.map(p => ({ portfolio_id: portfolioId, ...p }));
  const { data, error } = await supabase
    .from('positions')
    .insert(rows)
    .select('id, symbol, quantity, buy_price, buy_date, created_at');

  if (error) {
    console.error('[Supabase] seed demo positions →', error.code, error.message);
    throw error;
  }

  return data ?? [];
}

export async function loadPositions(portfolioId) {
  await requireSession();

  const { data, error } = await supabase
    .from('positions')
    .select('id, symbol, quantity, buy_price, buy_date, created_at')
    .eq('portfolio_id', portfolioId)
    .order('created_at');

  if (error) {
    console.error('[Supabase] SELECT positions →', error.code, error.message);
    throw error;
  }

  return data ?? [];
}

export async function addPosition(portfolioId, { symbol, quantity, buy_price, buy_date }) {
  await requireSession();

  const payload = { portfolio_id: portfolioId, symbol, quantity, buy_price };
  if (buy_date) payload.buy_date = buy_date;

  const { data, error } = await supabase
    .from('positions')
    .insert(payload)
    .select('id')
    .single();

  if (error) {
    console.error('[Supabase] INSERT positions →', error.code, error.message);
    throw error;
  }

  return data.id;
}

export async function removePosition(positionId) {
  await requireSession();

  const { error } = await supabase
    .from('positions')
    .delete()
    .eq('id', positionId);

  if (error) {
    console.error('[Supabase] DELETE position →', error.code, error.message);
    throw error;
  }
}
